import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ItemList from "../components/ItemList";
import { EventBusEventsEnum } from "../enums/EventBusEventsEnum";
import EventBus from "../EventBus";
import Razorpay from "../integrations/Razorpay";
import CustomerService from "../services/customerService";
import { retrieveFromSessionStorage, saveToSessionStorage } from "../utils/utils";
import TopBar from "../components/common/TopBar";
import CardTitle from "../components/common/CardTitle";
import { RequestedForEnum } from "../enums/RequestedForEnum";
import RazorpayPayment from "../interfaces/razorpayPayment";
import { RazorpayTransactionStatesEnum } from "../enums/RazorpayTransactionStatesEnum";
import { PaymentStatusEnum } from "../enums/PaymentStatusEnum";
import { FirebaseCleverTabRecordEvent, FirebaseCleverTabSetProfile } from "../firebase";
import { EventNames } from "../firebase/EventNames";
import { ScreenNames } from "../firebase/ScreenNames";
import { EventProperties } from "../firebase/EventProperties";
import Customer from "../interfaces/customer";
import DiscountCouponExtensionService from "../services/discountCouponExtensionService";

const customerService = new CustomerService();
const discountCouponExtensionService = new DiscountCouponExtensionService();

export default function DiscountCouponExtensionPayment() {
    const [details, setDetails] = useState<any>();
    const [user, setUser] = useState<Customer | null>();
    const [loading, setLoading] = useState<boolean>(true);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const resolveExtensionRequest = (payload: any, targetId?: number) => {
        if (!payload) return null;
        if (payload?.expiryExtensionRequest) return payload.expiryExtensionRequest;
        if (Array.isArray(payload?.expiryExtensionRequests)) {
            const found = payload.expiryExtensionRequests.find((item: any) => Number(item?.id) === Number(targetId));
            return found || payload.expiryExtensionRequests[0] || null;
        }
        if (payload?.extendByDays !== undefined || payload?.status !== undefined) return payload;
        return null;
    };

    useEffect(() => {
        const token = searchParams.get("token") || retrieveFromSessionStorage("token");
        const extensionRequestId =
            searchParams.get("serviceId") ||
            searchParams.get("id") ||
            retrieveFromSessionStorage("discountCouponExtensionId");

        if (token && extensionRequestId) {
            saveToSessionStorage("token", token);
            saveToSessionStorage("discountCouponExtensionId", extensionRequestId);
            saveToSessionStorage("requestedFor", RequestedForEnum.COUPON_EXPIRY_EXTENSION);
            FirebaseCleverTabRecordEvent(EventNames.OKANE_PAGE_VIEWED, {
                [EventProperties.PAGE_NAME]: ScreenNames.DISCOUNT_COUPON_EXTENSION_PAYMENT,
            });
            return;
        }

        navigate("/expired");
    }, [navigate, searchParams]);

    useEffect(() => {
        setLoading(true);
        const token = searchParams.get("token") || retrieveFromSessionStorage("token");
        const extensionRequestId =
            Number(
                searchParams.get("serviceId") ||
                searchParams.get("id") ||
                retrieveFromSessionStorage("discountCouponExtensionId")
            );

        if (token && extensionRequestId) {
            (async function () {
                const customer = await customerService.getCustomerDetails();
                setUser(customer);
                const extensionData = await discountCouponExtensionService.getDiscountCouponExtensionDetails(
                    extensionRequestId
                );
                const extensionRequest = resolveExtensionRequest(extensionData, extensionRequestId);
                const paymentState = extensionRequest?.status || extensionRequest?.paymentStatus;
                if (![PaymentStatusEnum.NOT_PAID, PaymentStatusEnum.PAYMENT_IN_PROGRESS].includes(paymentState)) {
                    navigate("/expired", { replace: true });
                    return;
                }
                setDetails(extensionRequest);
                setLoading(false);
            })();
        } else {
            navigate("/expired");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const RenderDiscountedPrice = (props: any) => {
        return (
            <span style={{ margin: 0, marginBottom: 2 }}>
                <span style={{ color: "#FF941A" }}>
                    {`Rs ${props?.data?.amount || props?.data?.razorpayOrder?.amount || "0"} `}
                </span>
            </span>
        );
    };

    const onSuccessfulPayment = async (extensionRequestId: number, paymentObj: any) => {
        const razorpayPayment: RazorpayPayment = {
            razorpayOrderId: paymentObj.razorpay_order_id,
            razorpayPaymentId: paymentObj.razorpay_payment_id,
            razorpaySignature: paymentObj.razorpay_signature,
            state: RazorpayTransactionStatesEnum.COMPLETED,
        };
        await discountCouponExtensionService.updatePaymentStatus(extensionRequestId, razorpayPayment);
        window.location.replace(`/connecting-securely`);
    };

    const onClickPay = async () => {
        if (!details) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: "Something Went Wrong",
            });
            return;
        }
        const transactionV3Obj = await discountCouponExtensionService.initiatePayment(details!.id);
        if (!transactionV3Obj) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: "Something Went Wrong",
            });
            return;
        }
        saveToSessionStorage("transactionId", details!.id);
        saveToSessionStorage("requestedFor", RequestedForEnum.COUPON_EXPIRY_EXTENSION);
        const extensionRequest = resolveExtensionRequest(transactionV3Obj, details!.id) || details;
        const razorpayOrder = extensionRequest?.razorpayOrder || transactionV3Obj?.razorpayOrder;
        const paymentState = extensionRequest?.status || extensionRequest?.paymentStatus || transactionV3Obj?.paymentStatus;
        if (paymentState === PaymentStatusEnum.PAID) {
            navigate("/success", { replace: true });
            return;
        }
        if (!razorpayOrder?.razorpayOrderId) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: "Unable to initiate payment",
            });
            return;
        }
        const rzpy = new Razorpay();
        const customer = await customerService.getCustomerDetails();
        customer && FirebaseCleverTabSetProfile(customer);
        FirebaseCleverTabRecordEvent(EventNames.OKANE_PAY_NOW_CLICKED, {
            [EventProperties.REFERENCE_MODEL_NAME]: razorpayOrder?.referenceModelName,
            [EventProperties.AMOUNT]: razorpayOrder?.amount,
        });
        await rzpy.initiateRazorPayPaymentFlow(
            {
                id: details!.id,
                amount: Number(razorpayOrder?.amount),
                orderId: razorpayOrder?.razorpayOrderId,
                email: customer?.email || "",
                fname: customer?.fname || "",
                mobile: customer?.mobile || "",
            },
            onSuccessfulPayment
        );
    };

    // @ts-ignore
    return (
        <div
            className="d-flex-column-center"
            style={{
                width: "100%",
                height: "100%",
                maxWidth: "500px",
                justifyContent: "flex-start",
                gap: 20,
            }}
        >
            <div className="d-flex-column-center" style={{ width: "100%", gap: 20 }}>
                <TopBar title={"Summary"} />
                <div
                    className="d-flex-column-center"
                    style={{
                        width: "100%",
                        height: "100%",
                        gap: 20,
                        justifyContent: "flex-start",
                    }}
                >
                    <CardTitle title={"Extension Details"} />
                    {!loading && (
                        <div
                            className="d-flex-column-center"
                            style={{
                                width: "95%",
                                justifyContent: "flex-start",
                                alignItems: "flex-start",
                                gap: 10,
                            }}
                        >
                            <div className="card">
                                <ItemList
                                    title="Total Amount"
                                    action={() => `Rs ${details?.amount || details?.razorpayOrder?.amount || "..."}`}
                                />
                                <div
                                    style={{
                                        borderTop: "dashed",
                                        borderWidth: 0.5,
                                        color: "#BDBDBD",
                                    }}
                                />
                                <ItemList
                                    title="Customer Name"
                                    action={() => [user?.fname, user?.lname].filter(Boolean).join(" ") || "..."}
                                />
                                <ItemList
                                    title="Customer Phone Number"
                                    action={() => `${user?.mobile || "..."}`}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div style={{ width: "100%" }}>
                <div
                    className="d-flex-column-center"
                    style={{
                        width: "95%",
                        padding: "10px",
                    }}
                >
                    {!loading && (
                        <ItemList
                            title={<RenderDiscountedPrice data={details} />}
                            subtitle={"Net payable amount"}
                            action={() => (
                                <button
                                    style={{
                                        color: "white",
                                        backgroundColor: "black",
                                        borderRadius: "9px",
                                        padding: "12px 22px",
                                        cursor: "pointer",
                                        border: "none",
                                    }}
                                    onClick={onClickPay}
                                >
                                    Pay Now
                                </button>
                            )}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
